import {Form, FormSubmitHandler} from 'platform/components';

import {VehicleTypeEnumObject} from '@dms/api/shared';
import {VehicleSeriesDefinition} from '@dms/teas';

import {useVehicleSeriesDefinitionContext} from '../hooks/useVehicleSeriesDefinitionContext';
import {VehicleSeriesDefinitionFormWrapperProps, VehicleSeriesDefinitionFormType} from '../types';
import {
  VehicleSeriesDefinitionFormValidations,
  VehicleSeriesSystemDefinitionFormValidations,
} from './validations';
import {VehicleSeriesDefinitionFormFields} from './VehicleSeriesDefinitionFormFields';

const transformInitialValues = (
  values: VehicleSeriesDefinition | undefined
): VehicleSeriesDefinitionFormType | undefined => {
  if (!values) {
    return undefined;
  }

  const {id: _id, isSystem: _isSystem, ...rest} = values;

  return {
    ...rest,
    vehicleType: rest.vehicleType as VehicleTypeEnumObject,
    makes: rest.makes ?? [],
  };
};

function getNormalizedMakes(values: VehicleSeriesDefinitionFormType): string[] | null {
  if (!('makes' in values) || !Array.isArray(values.makes)) {
    return null;
  }
  return values.makes.length === 0 ? null : values.makes;
}

const transformRequestBody = (
  values: VehicleSeriesDefinitionFormType
): VehicleSeriesDefinitionFormType => {
  const makes = getNormalizedMakes(values);
  return {...values, makes};
};

export const VehicleSeriesDefinitionFormWrapper = ({
  FormWrapper,
  defaultValues,
  initialValues,
  onSubmit,
  renderFooter,
  children,
  cancelRoute,
}: VehicleSeriesDefinitionFormWrapperProps) => {
  const {isSystem} = useVehicleSeriesDefinitionContext();

  const formValues = transformInitialValues(initialValues);

  const handleSubmit: FormSubmitHandler<VehicleSeriesDefinitionFormType> = async (
    values,
    setErrors,
    _reset,
    blockerContext
  ) => {
    const requestBody = transformRequestBody(values);
    await onSubmit?.(requestBody, setErrors, blockerContext);
  };

  const schema = isSystem
    ? VehicleSeriesSystemDefinitionFormValidations
    : VehicleSeriesDefinitionFormValidations;

  return (
    <Form<VehicleSeriesDefinitionFormType>
      onSubmit={handleSubmit}
      experimentalZodSchema={schema}
      defaultValues={defaultValues}
      values={formValues}
      shouldWatchForUnsavedChanges
    >
      {(control, formApi, navigateWithoutBlocker) => (
        <>
          {FormWrapper ? (
            <FormWrapper>
              <VehicleSeriesDefinitionFormFields control={control} formApi={formApi} />
            </FormWrapper>
          ) : (
            <VehicleSeriesDefinitionFormFields control={control} formApi={formApi} />
          )}

          {renderFooter?.({navigateWithoutBlocker, cancelRoute}) ?? children}
        </>
      )}
    </Form>
  );
};
